import React from 'react';
import type { Agent } from '../../types';
import { cn, getAgentAvatarClass } from '../../utils';

type AgentAvatarLike = Pick<Agent, 'name' | 'avatar'>;

interface AgentAvatarProps {
  agent: AgentAvatarLike;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<AgentAvatarProps['size']>, string> = {
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-sm',
  lg: 'h-12 w-12 text-lg',
};

function isImageAvatar(value?: string) {
  if (!value) return false;
  return /^(?:https?:\/\/|data:|\/)/i.test(value.trim());
}

function getFallbackLabel(agent: AgentAvatarLike) {
  const avatar = String(agent.avatar || '').trim();
  if (avatar && !isImageAvatar(avatar)) {
    return avatar;
  }

  return String(agent.name || '?').trim().charAt(0).toUpperCase() || '?';
}

export function AgentAvatar({ agent, size = 'md', className }: AgentAvatarProps) {
  const [imageFailed, setImageFailed] = React.useState(false);
  const avatar = String(agent.avatar || '').trim();
  const name = String(agent.name || 'Agent').trim() || 'Agent';
  const canRenderImage = isImageAvatar(avatar) && !imageFailed;

  React.useEffect(() => {
    setImageFailed(false);
  }, [avatar]);

  if (canRenderImage) {
    return (
      <img
        src={avatar}
        alt={name}
        loading="lazy"
        onError={() => setImageFailed(true)}
        className={cn(
          'rounded-full object-cover ring-1 ring-white/10',
          SIZE_CLASSES[size],
          className,
        )}
      />
    );
  }

  return (
    <div
      role="img"
      aria-label={`${name} avatar`}
      className={cn(
        'flex shrink-0 select-none items-center justify-center rounded-full font-semibold text-white ring-1 ring-white/10',
        SIZE_CLASSES[size],
        getAgentAvatarClass(name),
        className,
      )}
    >
      {getFallbackLabel(agent)}
    </div>
  );
}

export default AgentAvatar;
